import {Stack, Typography} from '@mui/material';
import React from 'react';

import {OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {SummaryCard, SummaryCardSection} from 'presentation/components';
import {translations} from 'presentation/translations-new';
import {formatMasks, formatText} from 'presentation/utils';

import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {AppointmenInstructionsAlertInfo} from 'presentation/modules/booking/shared/ui/components/AppointmenInstructionsAlertInfo';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {ReviewCard} from 'presentation/modules/booking/shared/ui/components/ReviewCard';
import {useTelehealthSummarySectionGroup} from './hooks/useTelehealthSummarySectionGroup';
import {TelehealthSummarySectionGroupProps} from './types';

const {appointmentSummary, beneficiaryData, cardCode} =
	translations['pt-br'].privateFlow.bookingFlow;
const {attendance, specialty, contactInfo, beneficiaryPlan} = translations['pt-br'].commons;
const {appointmentInstructions} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails;

export function TelehealthSummarySectionGroup({
	stepLabels,
	activeStep,
	handleChangeModals,
	disableGutters,
	showTitle,
	noBorderRadius,
}: TelehealthSummarySectionGroupProps) {
	const {
		showSpecialtyField,
		bookingTheme,
		colors,
		cdUsuario,
		beneficiaryCardPlanData,
		currentEmail,
		currentPhone,
		isLastStep,
		isRebooking,
		specialtyCode,
		specialtyName,
		bookingData,
		showUnitField,
	} = useTelehealthSummarySectionGroup({
		activeStep,
		stepLabels,
	});

	if (!isLastStep) {
		return (
			<SummaryCard
				noBorderRadius={noBorderRadius}
				showTitle={showTitle}
				disableGuters={disableGutters}
				title={appointmentSummary}
				hideAction>
				<SummaryCardSection
					hideAction={true}
					disableGutters={disableGutters}
					hasDivider
					subheader={contactInfo}
					primaryText={formatMasks('phone', currentPhone)}
					iconName="phone-android-filled"
					disableSecondaryTextFormat
					secondaryData={{
						primaryText: currentEmail,
						iconName: 'mail-filled',
					}}
				/>
				{showSpecialtyField ? (
					<SummaryCardSection
						hideAction={isRebooking}
						handleAction={() => handleChangeModals(ChangeFlowVariantEnum.SPECIALTY)}
						disableGutters={disableGutters}
						hasDivider
						subheader={specialty}
						primaryText={specialtyName}
						iconName={bookingTheme.iconName}
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_ESPECIALIDADE}${FlowEnum.TELEHEALTH}`}
					/>
				) : null}

				{showUnitField ? (
					<SummaryCardSection
						hideAction
						disableGutters={disableGutters}
						hasDivider
						subheader={attendance}
						primaryText={bookingData.unitName}
						iconName={'remember-me-filled'}
						secondaryText={bookingData.unitAddress}
					/>
				) : null}
			</SummaryCard>
		);
	}

	return (
		<Stack gap={2}>
			<Typography variant="subtitle2" color={colors.light.text.secondary}>
				{beneficiaryData}
			</Typography>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={beneficiaryPlan} />
				<Stack gap={'16px'}>
					<ReviewCard.Content
						iconName={'account-circle-filled'}
						primaryText={formatText(beneficiaryCardPlanData.userName)}
					/>
					<ReviewCard.Content
						iconName={'hapvida-logo'}
						primaryText={formatText(beneficiaryCardPlanData.planName)}
						secondaryText={`${cardCode}${cdUsuario}`}
					/>
				</Stack>
			</ReviewCard.Root>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={contactInfo} />
				<Stack gap={'8px'}>
					<ReviewCard.Content
						iconName={'phone-android-filled'}
						primaryText={formatMasks('phone', currentPhone)}
					/>
					<ReviewCard.Content
						iconName={'mail-filled'}
						primaryText={currentEmail}
						disablePrimaryTextFormat
					/>
				</Stack>
			</ReviewCard.Root>
			<Stack gap={2}>
				<Typography variant="subtitle2" color={colors.light.text.secondary}>
					{appointmentInstructions.title}
				</Typography>
				<AppointmenInstructionsAlertInfo
					bookingType={BookingTypeEnum.TELEHEALTH}
					specialtyCode={specialtyCode}
					specialtyName={specialtyName}
				/>
			</Stack>
		</Stack>
	);
}
